import { useCallback, useMemo, useState } from "react";
import { formatearNumeros } from "../../../utils/formatters";
import type {
  DetallesClienteData,
  DetallesClienteMetricas,
  DetallesClienteModalProps,
  DetalleTablaHeader,
  DetalleTablaRow,
  UbicacionCliente,
  UseDetallesClienteModalReturn,
} from "./types";

const DATA_TABLA_HEADER: DetalleTablaHeader[] = [
  { titulo: "Fecha Inicial" },
  { titulo: "fecha final" },
  { titulo: "forma de pago" },
  { titulo: "valor del prestamo" },
  { titulo: "% interes" },
  { titulo: "deuda total" },
  { titulo: "cuota diaria" },
  { titulo: "abono hoy" },
  { titulo: "valor pico" },
  { titulo: "abono total" },
  { titulo: "total cuotas" },
  { titulo: "cuotas pagadas" },
  { titulo: "cuotas restantes" },
  { titulo: "cuotas atrasadas" },
  { titulo: "debe a la fecha" },
  { titulo: "deuda restante" },
];

const normalizarNumero = (value: number | string | null | undefined): number => {
  const parsedValue = Number(value ?? 0);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const normalizarTexto = (value: string | null | undefined): string => {
  return value ?? "";
};

const parsearUbicacionCliente = (ubicacion: string | undefined): UbicacionCliente => {
  const [latitudStr = "0", longitudStr = "0"] = (ubicacion ?? "").split(", ");

  return {
    latitud: parseFloat(latitudStr) || 0,
    longitud: parseFloat(longitudStr) || 0,
  };
};

const normalizarDetallesCliente = (cliente: DetallesClienteData): DetallesClienteData => {
  return {
    ...cliente,
    abono: normalizarNumero(cliente.abono),
    cpf: normalizarTexto(cliente.cpf),
    cuotasAtrasadas: normalizarNumero(cliente.cuotasAtrasadas),
    cuotasPactadas: normalizarNumero(cliente.cuotasPactadas),
    descripcion: normalizarTexto(cliente.descripcion),
    direccion: normalizarTexto(cliente.direccion),
    direccionCobro: normalizarTexto(cliente.direccionCobro),
    fechaActual: normalizarTexto(cliente.fechaActual),
    fechaFinal: normalizarTexto(cliente.fechaFinal),
    formaDePago: normalizarTexto(cliente.formaDePago),
    imageTienda: normalizarTexto(cliente.imageTienda),
    imageUrl: normalizarTexto(cliente.imageUrl),
    nombreCliente: normalizarTexto(cliente.nombreCliente),
    pagoDiario: normalizarNumero(cliente.pagoDiario),
    porcentajeInteres: normalizarNumero(cliente.porcentajeInteres),
    telefono: normalizarTexto(cliente.telefono),
    totalAbono: normalizarNumero(cliente.totalAbono),
    ubicacion: normalizarTexto(cliente.ubicacion),
    valorPico: normalizarNumero(cliente.valorPico),
    valorPrestamo: normalizarNumero(cliente.valorPrestamo),
  };
};

const calcularMetricasCliente = (cliente: DetallesClienteData): DetallesClienteMetricas => {
  const totalCuotas = normalizarNumero(cliente.cuotasPactadas);
  const cuotaDiaria = normalizarNumero(cliente.pagoDiario);
  const totalAbono = normalizarNumero(cliente.totalAbono);
  const cuotasAtrasadas = Math.max(0, normalizarNumero(cliente.cuotasAtrasadas));
  const creditoTotal = cuotaDiaria * totalCuotas;
  const deudaActual = creditoTotal - totalAbono;
  const valorPico = cuotaDiaria !== 0 ? totalAbono % cuotaDiaria : 0;
  const valorPicoDisplay = valorPico !== 0 ? Number(valorPico.toFixed(1)) : null;
  const cuotasPagadas = cuotaDiaria !== 0 ? Math.floor(totalAbono / cuotaDiaria) : 0;
  const cuotasRestantes = totalCuotas - cuotasPagadas;
  const deudaFecha =
    cuotasAtrasadas > 0 ? cuotaDiaria * cuotasAtrasadas - normalizarNumero(cliente.valorPico) : 0;

  return {
    creditoTotal,
    cuotaDiaria,
    cuotasAtrasadas,
    cuotasPagadas,
    cuotasRestantes,
    deudaActual,
    deudaFecha,
    totalCuotas,
    valorPicoDisplay,
  };
};

const crearDetalleTablaRows = (
  cliente: DetallesClienteData,
  metricas: DetallesClienteMetricas,
  formatearNumeros: (numero: number | null | undefined) => string,
): DetalleTablaRow[] => {
  return [
    { valor: cliente.fechaActual ?? "" },
    { valor: cliente.fechaFinal ?? "" },
    { valor: cliente.formaDePago ?? "" },
    { valor: `R$${formatearNumeros(cliente.valorPrestamo)}` },
    { valor: `${cliente.porcentajeInteres ?? 0}%` },
    { valor: `R$${formatearNumeros(metricas.creditoTotal)}` },
    { valor: `R$${formatearNumeros(metricas.cuotaDiaria)}` },
    { valor: `R$${formatearNumeros(cliente.abono)}` },
    {
      valor: `R$${formatearNumeros(metricas.valorPicoDisplay ?? 0)}`,
    },
    {
      valor: `R$${formatearNumeros(cliente.totalAbono)}`,
    },
    { valor: metricas.totalCuotas },
    { valor: metricas.cuotasPagadas },
    { valor: metricas.cuotasRestantes },
    { valor: metricas.cuotasAtrasadas },
    { valor: `R$${formatearNumeros(metricas.deudaFecha)}` },
    { valor: `R$${formatearNumeros(metricas.deudaActual)}` },
  ];
};

const crearEstadoDerivado = (
  cliente: DetallesClienteData,
  formatearNumeros: (numero: number | null | undefined) => string,
) => {
  const clienteNormalizado = normalizarDetallesCliente(cliente);
  const ubicacion = parsearUbicacionCliente(clienteNormalizado.ubicacion);
  const metricas = calcularMetricasCliente(clienteNormalizado);

  return {
    cliente: clienteNormalizado,
    detalleTablaRows: crearDetalleTablaRows(clienteNormalizado, metricas, formatearNumeros),
    mapUrl: `https://www.google.com/maps?q=${ubicacion.latitud},${ubicacion.longitud}`,
    tieneFotoPerfil: Boolean(clienteNormalizado.imageUrl),
    tieneFotoTienda: Boolean(clienteNormalizado.imageTienda),
    tieneUbicacion: Boolean(clienteNormalizado.ubicacion),
  };
};

export const useDetallesClienteModal = ({
  selectedDetallesCliente,
  setVerDetallesCliente,
}: DetallesClienteModalProps): UseDetallesClienteModalReturn | null => {
  const [verFotoPerfil, setVerFotoPerfil] = useState(false);
  const [verFotoDeTienda, setVerFotoDeTienda] = useState(false);
  const [mostrarCartonDigital, setMostrarCartonDigital] = useState(false);
  const [mostrarHistorial, setMostrarHistorial] = useState(false);
  const cliente = selectedDetallesCliente;

  const handleClose = useCallback(() => {
    setVerDetallesCliente(false);
  }, [setVerDetallesCliente]);

  const handleOpenFotoDeTienda = useCallback(() => {
    setVerFotoDeTienda(true);
  }, []);

  const handleOpenFotoPerfil = useCallback(() => {
    setVerFotoPerfil(true);
  }, []);

  const handleOpenCartonDigital = useCallback(() => {
    setMostrarCartonDigital(true);
  }, []);

  const handleOpenHistorial = useCallback(() => {
    setMostrarHistorial(true);
  }, []);

  const handleCloseFotoDeTienda = useCallback(() => {
    setVerFotoDeTienda(false);
  }, []);

  const handleCloseFotoPerfil = useCallback(() => {
    setVerFotoPerfil(false);
  }, []);

  const handleCloseCartonDigital = useCallback(() => {
    setMostrarCartonDigital(false);
  }, []);

  const handleCloseHistorial = useCallback(() => {
    setMostrarHistorial(false);
  }, []);

  const estadoDerivado = useMemo(
    () => crearEstadoDerivado(cliente ?? {}, formatearNumeros),
    [cliente],
  );

  if (!cliente) {
    return null;
  }

  return {
    cliente: estadoDerivado.cliente,
    dataTablaHeader: DATA_TABLA_HEADER,
    detalleTablaRows: estadoDerivado.detalleTablaRows,
    handleClose,
    handleCloseCartonDigital,
    handleCloseFotoPerfil,
    handleCloseFotoDeTienda,
    handleCloseHistorial,
    handleOpenCartonDigital,
    handleOpenFotoPerfil,
    handleOpenFotoDeTienda,
    handleOpenHistorial,
    mapUrl: estadoDerivado.mapUrl,
    mostrarCartonDigital,
    mostrarHistorial,
    tieneFotoPerfil: estadoDerivado.tieneFotoPerfil,
    tieneFotoTienda: estadoDerivado.tieneFotoTienda,
    tieneUbicacion: estadoDerivado.tieneUbicacion,
    verFotoPerfil,
    verFotoDeTienda,
  };
};
